/**
 * Pure reducer: folds an `OrchestrationEvent` into an `AgentRunState`.
 *
 * Used by the doc-writer to translate SSE events into a serializable
 * state shape stored on the `affine:agent-stages` block. Kept pure so
 * the state machine can be unit-tested without spinning up BlockSuite.
 */

import type {
  AgentProgressEvent,
  AgentStartedEvent,
  AgentType,
  CitationEvent,
  ErrorEvent,
  OrchestrationEvent,
  PlanStartEvent,
  StepCompleteEvent,
  StepErrorEvent,
  StepIcon,
  StepProgressEvent,
  StepStartEvent,
  SynthesisProgressEvent,
  SynthesisStartEvent,
} from '../types';
import {
  type AgentRunState,
  createEmptyAgentRunState,
  type StageEntry,
  SYNTHESIS_STAGE_ID,
} from './agent-run-state';

const AGENT_ICON: Record<AgentType, StepIcon> = {
  explore: 'file',
  'literature-search': 'search',
  'data-analysis': 'chart',
  synthesis: 'sparkles',
  'domain-expert': 'bookmark',
};

function pickIconForStep(
  agentType: string | undefined,
  title: string
): StepIcon {
  if (
    agentType &&
    Object.prototype.hasOwnProperty.call(AGENT_ICON, agentType)
  ) {
    return AGENT_ICON[agentType as AgentType];
  }
  const lower = (title || '').toLowerCase();
  if (lower.includes('search')) return 'search';
  if (lower.includes('verif') || lower.includes('citation')) return 'bookmark';
  if (lower.includes('analy') || lower.includes('data')) return 'chart';
  if (lower.includes('synthes')) return 'sparkles';
  if (lower.includes('read') || lower.includes('file')) return 'file';
  return 'loading';
}

function cloneState(state: AgentRunState): AgentRunState {
  return {
    ...state,
    subQuestions: [...state.subQuestions],
    stages: state.stages.map(s => ({ ...s, sources: [...s.sources] })),
    agentsSeen: [...state.agentsSeen],
  };
}

function ensureInit(state: AgentRunState | null): AgentRunState {
  return state ?? createEmptyAgentRunState('');
}

function markPriorActiveDone(stages: StageEntry[], now: number): StageEntry[] {
  return stages.map(s =>
    s.status === 'active'
      ? { ...s, status: 'done' as const, endedAt: s.endedAt ?? now }
      : s
  );
}

function updateStage(
  state: AgentRunState,
  stageId: string,
  mutate: (s: StageEntry) => StageEntry
): AgentRunState {
  const next = cloneState(state);
  const idx = next.stages.findIndex(s => s.id === stageId);
  if (idx >= 0) {
    next.stages[idx] = mutate(next.stages[idx]);
  }
  return next;
}

export interface ReduceOptions {
  /** Injected clock for deterministic tests. */
  now?: () => number;
}

export function reduce(
  state: AgentRunState | null,
  event: OrchestrationEvent,
  options: ReduceOptions = {}
): AgentRunState {
  const now = options.now ? options.now() : Date.now();

  switch (event.type) {
    case 'plan-start':
      return applyPlanStart(event, now);
    case 'plan-ready':
      return ensureInit(state);
    case 'step-start':
      return applyStepStart(ensureInit(state), event, now);
    case 'step-progress':
      return applyStepProgress(ensureInit(state), event);
    case 'step-complete':
      return applyStepComplete(ensureInit(state), event, now);
    case 'step-error':
      return applyStepError(ensureInit(state), event, now);
    case 'synthesis-start':
      return applySynthesisStart(ensureInit(state), event, now);
    case 'synthesis-progress':
      return applySynthesisProgress(ensureInit(state), event);
    case 'complete':
      return applyComplete(ensureInit(state), now);
    case 'error':
      return applyError(ensureInit(state), event, now);
    case 'citation':
      return applyCitation(ensureInit(state), event);
    case 'agent-event': {
      const inner = event.event;
      if (inner.type === 'started') {
        return applyAgentStarted(ensureInit(state), event as AgentStartedEvent);
      }
      if (inner.type === 'progress') {
        return applyAgentProgress(
          ensureInit(state),
          event as AgentProgressEvent
        );
      }
      return ensureInit(state);
    }
    default:
      return ensureInit(state);
  }
}

function applyPlanStart(event: PlanStartEvent, now: number): AgentRunState {
  return {
    ...createEmptyAgentRunState(event.query, now),
    planId: event.planId,
    mode: event.mode,
    subQuestions: event.subQuestions ? [...event.subQuestions] : [],
  };
}

function applyStepStart(
  state: AgentRunState,
  event: StepStartEvent,
  now: number
): AgentRunState {
  const next = cloneState(state);
  next.stages = markPriorActiveDone(next.stages, now);
  next.stages.push({
    id: event.stepId,
    icon: pickIconForStep(event.agentType, event.title),
    title: event.title || 'Working',
    status: 'active',
    startedAt: now,
    sources: [],
    agentType: event.agentType,
  });
  return next;
}

function applyStepProgress(
  state: AgentRunState,
  event: StepProgressEvent
): AgentRunState {
  return updateStage(state, event.stepId, s => ({
    ...s,
    lastSubStep: event.subStep.text,
    subStepDetail: event.subStep.detail,
  }));
}

function applyStepComplete(
  state: AgentRunState,
  event: StepCompleteEvent,
  now: number
): AgentRunState {
  return updateStage(state, event.stepId, s => ({
    ...s,
    status: 'done',
    endedAt: now,
    sources: event.sources ? [...event.sources] : s.sources,
    citationsFound: event.citationsFound,
    lastSubStep: undefined,
    subStepDetail: undefined,
  }));
}

function applyStepError(
  state: AgentRunState,
  event: StepErrorEvent,
  now: number
): AgentRunState {
  return updateStage(state, event.stepId, s => ({
    ...s,
    status: 'error',
    endedAt: now,
    error: event.error,
  }));
}

function applySynthesisStart(
  state: AgentRunState,
  _event: SynthesisStartEvent,
  now: number
): AgentRunState {
  const next = cloneState(state);
  next.stages = markPriorActiveDone(next.stages, now);
  next.stages = next.stages.filter(s => s.id !== SYNTHESIS_STAGE_ID);
  next.stages.push({
    id: SYNTHESIS_STAGE_ID,
    icon: 'sparkles',
    title: 'Synthesizing answer',
    status: 'active',
    startedAt: now,
    sources: [],
    agentType: 'synthesis',
  });
  return next;
}

function applySynthesisProgress(
  state: AgentRunState,
  event: SynthesisProgressEvent
): AgentRunState {
  return updateStage(state, SYNTHESIS_STAGE_ID, s => ({
    ...s,
    lastSubStep: event.message,
  }));
}

function applyComplete(state: AgentRunState, now: number): AgentRunState {
  const next = cloneState(state);
  next.stages = markPriorActiveDone(next.stages, now);
  next.streaming = false;
  next.collapsed = true;
  next.endedAt = now;
  return next;
}

function applyError(
  state: AgentRunState,
  event: ErrorEvent,
  now: number
): AgentRunState {
  const next = cloneState(state);
  const lastActive = [...next.stages]
    .reverse()
    .find(s => s.status === 'active');
  if (lastActive) {
    next.stages = next.stages.map(s =>
      s.id === lastActive.id
        ? { ...s, status: 'error' as const, endedAt: now, error: event.error }
        : s
    );
  }
  next.streaming = false;
  next.collapsed = false;
  next.endedAt = now;
  next.errorMessage = event.error;
  return next;
}

function applyCitation(
  state: AgentRunState,
  _event: CitationEvent
): AgentRunState {
  const next = cloneState(state);
  next.totalCitations += 1;
  return next;
}

function applyAgentStarted(
  state: AgentRunState,
  event: AgentStartedEvent
): AgentRunState {
  const next = cloneState(state);
  const agentType = event.event.agentType;
  if (!next.agentsSeen.includes(agentType)) {
    next.agentsSeen.push(agentType);
  }
  return next;
}

function applyAgentProgress(
  state: AgentRunState,
  event: AgentProgressEvent
): AgentRunState {
  const lastActive = [...state.stages]
    .reverse()
    .find(s => s.status === 'active');
  if (!lastActive) return state;
  return updateStage(state, lastActive.id, s => ({
    ...s,
    lastSubStep: event.event.message,
  }));
}
